import { memo, useCallback } from 'react';

import { CheckIcon } from '@air/design-icons';

import { type BrandFontEntry } from '~/components/ShareModal/shareLinkDesignResolution';
import { getFontFamilyCssValue, useFontLoader } from '~/hooks/useFontLoader';

export type ShareDesignFontRowBrandOptionProps = {
  brandFont: BrandFontEntry;
  isSelected: boolean;
  onSelect: (brandFont: BrandFontEntry) => void;
};

/**
 * One brand-kit font entry in the font row's popover, its label rendered in the entry's own
 * typeface. Brand kits hold a handful of fonts at most, so each mounted option simply loads its
 * font (uploaded file via `@font-face`, built-in family via Google Fonts) for as long as the
 * popover is open — the label shows the fallback until the swap lands.
 */
export const ShareDesignFontRowBrandOption = memo(
  ({ brandFont, isSelected, onSelect }: ShareDesignFontRowBrandOptionProps) => {
    useFontLoader({ fontFamily: brandFont.family, fontFaceUrl: brandFont.fileUrl });

    const handleClick = useCallback(() => {
      onSelect(brandFont);
    }, [brandFont, onSelect]);

    return (
      <button
        className="flex h-8 items-center gap-2 rounded-4 px-2 text-left ads:text-primary text-14 text-gray-12 ads:hover:bg-hover-subtle hover:bg-gray-3"
        data-testid="SHARE_DESIGN_FONT_BRAND_OPTION"
        onClick={handleClick}
        type="button"
      >
        {/* Dynamic runtime family — inline style is the only option. */}
        <span className="min-w-0 flex-1 truncate" style={{ fontFamily: getFontFamilyCssValue(brandFont.family) }}>
          {brandFont.label}
        </span>
        {isSelected && <CheckIcon size="sm" className="shrink-0 ads:text-icon-primary text-blue-9" />}
      </button>
    );
  },
);

ShareDesignFontRowBrandOption.displayName = 'ShareDesignFontRowBrandOption';
